import React, { useEffect, useState } from 'react';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DollarCircleOutlined,
  UsergroupAddOutlined,
  PlusCircleOutlined,
  IdcardOutlined,
  LogoutOutlined,
  CustomerServiceOutlined,
  ApiOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu, theme, Tag } from 'antd';
import '../styles/AppLayout.scss';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';


const { Header, Sider, Content } = Layout;

// Define types for the props
interface LayoutProps {
  children?: React.ReactNode;
}

const AppLayout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();


  const siderStyle: React.CSSProperties = {
    overflow: 'auto',
    height: '100vh',
    position: 'fixed',
    insetInlineStart: 0,
    top: 0,
    bottom: 0,
    scrollbarWidth: 'thin',
    scrollbarColor: 'unset',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  };

  const navigate = useNavigate(); // React Router hook for navigation

  const handleNavigate = (path: string) => {
    navigate(path);  // Navigate to /create-pricing page
  };

  const location = useLocation(); // Get current location
  const [shouldHideButton, setShouldHideButton] = useState<boolean>(false);
  const [shouldHideBackground, setShouldHideBackground] = useState<string>('white');

  useEffect(() => {
    // Function to check if the current URL matches the path where we want to hide the button
    const shouldHideButton = location.pathname === '/create-pricing'; // Example: Hide on the '/create-pricing' page
    const shouldHideBackground = location.pathname === '/version-management' || location.pathname === '/edit-pricing-details' || location.pathname === '/home' ? 'transparent' : 'white'; // Example: Hide on the '/create-pricing' page
    setShouldHideButton(shouldHideButton);
    setShouldHideBackground(shouldHideBackground);
  }, [location]); // Run the effect when the location changes

  const handleLogout = () => {
    localStorage.removeItem('login_token');
    navigate("/login");
  };

  const app_env = process.env.REACT_APP_ENV
  const userFeatures: string[] = JSON.parse(localStorage.getItem("user_details") || "{}").features || [];
  const hasSowStudioAccess = ['FE_SOW_STUDIO', 'FE_SOW_STUDIO_PILOT', 'FE_ADMIN']
    .some((feature) => userFeatures.includes(feature));

  // Add this function to determine selected key based on current path
  const getSelectedKey = () => {
    const path = location.pathname;
    switch (path) {
      case '/home':
        return ['1'];
      case '/resource-request':
        return ['2'];
      case '/recommendation-engine':
        return ['3'];
      case '/sow-studio':
        return ['4'];
      case '/admin':
        return ['5'];
      case '/help-center':
        return ['6'];
      default:
        return ['1'];
    }
  };

  return (
    <Layout>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={siderStyle}
        theme="light"
      >
        <div
          className="logo-container"
          style={{ display: "flex", gap: "8px", alignItems: "center" }}
        >
          <img
            src={
              "https://cdn.prod.website-files.com/64c8fffb0e95cbc525815b79/6505803cb325f7caa9cef3c1_blend-mobile-logo-black.svg"
            }
            alt="Blend Logo"
            style={{ marginRight: "auto", marginLeft: "auto", width: "50px" }}
          />
          {!collapsed && app_env && <Tag color="volcano">{app_env}</Tag>}
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={getSelectedKey()}
          items={[
            {
              key: "1",
              icon: <DollarCircleOutlined />,
              label: "Home",
              onClick: () =>
                location.pathname.includes("/home")
                  ? window.location.reload()
                  : handleNavigate("/home"),
            },
            {
              key: "2",
              icon: <UsergroupAddOutlined />,
              label: "Resource Requests",
              onClick: () => handleNavigate("/resource-request"),
            },
            ...((JSON.parse(
              localStorage.getItem("user_details") || "{}"
            ).features || []).includes("AUDIT_RESOURCE_CHANGES")
              ? [
                  {
                    key: "3",
                    icon: <ApiOutlined />,
                    label: "Smart Profiler",
                    onClick: () => handleNavigate("/recommendation-engine"),
                  },
                ]
              : []),
            ...(hasSowStudioAccess ? [{
              key: "4",
              icon: <FileTextOutlined />,
              label: "SOW Studio",
              onClick: () => navigate("/sow-studio", {
                state: { openDealSearch: true },
              }),
            }] : []),
            // Only show admin menu if user has admin access
            ...((JSON.parse(
              localStorage.getItem("user_details") || "{}"
            ).features || []).includes("FE_ADMIN")
              ? [
                  {
                    key: "5",
                    icon: <IdcardOutlined />,
                    label: "Admin Center",
                    onClick: () => handleNavigate("/admin"),
                  },
                ]
              : []),
            {
              key: "6",
              icon: <CustomerServiceOutlined />,
              label: "Help Center",
              onClick: () => handleNavigate("/help-center"),
            },
          ]}
        />
      </Sider>
      <Layout style={{ marginInlineStart: collapsed ? 80 : 200 }}>
        <Header
          style={{
            padding: 0,
            background: colorBgContainer,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: "16px",
                width: 64,
                height: 64,
              }}
            />
            <Tag color="yellow">
              Pricing App Phase I for Americas (Non-IS, Non-Data Deals) Only
            </Tag>
            {collapsed && app_env && <Tag color="volcano">{app_env}</Tag>}
          </div>
          <div>
            {!shouldHideButton && (
              <Button
                title="Create New Pricing"
                icon={<PlusCircleOutlined />}
                onClick={() => handleNavigate("/create-pricing")}
                style={{
                  fontSize: "16px",
                  marginRight: "2.5rem",
                }}
                className="custom-button"
              >
                Create New Pricing
              </Button>
            )}
            <Button
              title="Logout"
              icon={<LogoutOutlined />}
              onClick={() => handleLogout()}
              style={{
                fontSize: "16px",
                marginRight: "2.5rem",
              }}
              type="primary"
            >
              Logout
            </Button>
          </div>
        </Header>
        <Content
          style={{
            margin: "24px 16px",
            padding: 24,
            minHeight: 280,
            background: shouldHideBackground,
            borderRadius: borderRadiusLG,
            overflow: "initial",
          }}
        >
          {children || <Outlet />}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
